import { DataTable } from "@/components/data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { columns } from "../_misc/columns";
import { useGetCardTransactions, useGetDeposits } from "@/hooks/use-queries";
import TopBar from "@/components/topbar";
import { cardColumns } from "../_misc/cardColumns";
import TransactionList from "../_misc/TransactionList";

const Transactions = () => {
  const [tab, setTab] = useState<"wallet" | "cards">("wallet");
  const [paginatedData, setPaginationData] = useState([]);
  const [paginationParams, setPaginationParams] = useState({
    pageIndex: tab === "wallet" ? 1 : 0,
    pageSize: 10,
  });
  const [pageCount, setPageCount] = useState(1);
  const { data: transactions, isLoading: isLoadingTransactions } =
    useGetDeposits({
      page: paginationParams.pageIndex,
      limit: paginationParams.pageSize,
    });
  const { data: cardTransactions, isLoading: isLoadingCardTransactions } =
    useGetCardTransactions({
      page: 0,
      limit: 150,
    });

  const handleTabChange = (value) => {
    setTab(value);
    setPaginationParams({
      pageIndex: value === "wallet" ? 1 : 0,
      pageSize: 10,
    });
  };

  const handlePageChange = (page: number) => {
    if (page > -1 && page <= pageCount) {
      setPaginationParams({
        pageIndex: page,
        pageSize: paginationParams.pageSize,
      });
    }
  };

  useEffect(() => {
    if (tab === "wallet") {
      setPageCount(transactions?.totalPages || 1);
    } else {
      setPageCount(cardTransactions?.total || 1);
    }
  }, [tab, transactions, cardTransactions]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (tab === "cards") {
      setPaginationData(
        cardTransactions?.data.slice(
          paginationParams.pageIndex * 10,
          (paginationParams.pageIndex + 1) * 10,
        ),
      );
    }
  }, [cardTransactions, paginationParams]);

  return (
    <div className="">
      <TopBar title="Transactions" />
      <Tabs
        onValueChange={(value: "wallet" | "cards") => {
          handleTabChange(value);
        }}
        value={tab}
        className="font-poppins px-4 py-4 lg:bg-transparent"
      >
        <TabsList className="w-full border border-[#746e82] bg-white/10 p-0 lg:w-fit">
          <TabsTrigger
            value="wallet"
            className="data-[state=active]:text-primary-500 rounded-l-lg rounded-r-none px-6 text-[#E9F9FF] data-[state=active]:bg-[linear-gradient(128.62deg,#9191CB_11.02%,#EDD6FF_93.11%)]"
          >
            Wallet
          </TabsTrigger>
          <TabsTrigger
            value="cards"
            className="data-[state=active]:text-primary-500 rounded-l-none rounded-r-lg px-6 text-[#E9F9FF] data-[state=active]:bg-[linear-gradient(128.62deg,#9191CB_11.02%,#EDD6FF_93.11%)]"
          >
            Cards
          </TabsTrigger>
        </TabsList>
        <div className="bg-bg-primary/90 mt-4 rounded-2xl p-4 text-white backdrop-blur-sm lg:row-start-auto">
          <div className="flex items-center justify-between bg-white/5"></div>

          <TabsContent value="wallet" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={columns}
                data={transactions?.data ?? []}
                isLoading={isLoadingTransactions}
                pagination={{
                  ...paginationParams,
                  pageIndex: paginationParams.pageIndex + 1,
                }}
                setPagination={setPaginationParams}
                pageCount={pageCount}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingTransactions}
                transactions={transactions?.data ?? []}
                currentPage={paginationParams.pageIndex}
                totalPages={pageCount}
                handlePageChange={handlePageChange}
              />
            </div>
          </TabsContent>
          <TabsContent value="cards" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={cardColumns}
                data={cardTransactions?.data ?? []}
                isLoading={isLoadingCardTransactions}
                pagination={{
                  ...paginationParams,
                  pageIndex: paginationParams.pageIndex + 1,
                }}
                setPagination={setPaginationParams}
                pageCount={pageCount}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingCardTransactions}
                transactions={paginatedData ?? []}
                type="cards"
                currentPage={paginationParams.pageIndex}
                totalPages={pageCount}
                handlePageChange={handlePageChange}
              />
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default Transactions;
